import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  useCirclePlugSignUp,
  useInitOtp,
  useResetPassword,
  useVerifyOtp,
} from "../../lib/services/auth-service";
import { setLoggedinUser } from "../../lib/util";
import { useAppDispatch } from "../slice/hooks";
import { logInUser } from "../slice/auth-slice";

const OtpForm = ({
  sessionId,
  token: initialToken,
  otpData,
  signUpData,
  target,
  onBack,
  onLoginSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [currentOtpData, setCurrentOtpData] = useState(otpData);
  const [remainingTime, setRemainingTime] = useState(
    Math.ceil(otpData.waitTime)
  );
  const [token, setTokenState] = useState(initialToken);
  const [showPasswordResetForm, setShowPasswordResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleOtpChange = useCallback((e) => {
    setOtp(e.target.value);
    setError("");
  }, []);

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const { isLoading, mutate } = useVerifyOtp({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      const { user, token, tokenExpiryDate, tokenType, sessionId } =
        response.data;
      if (target === "login") {
        const loggedInUser = {
          sessionId,
          tokenExpiryDate,
          token,
          user: user,
        }
        setLoggedinUser(loggedInUser);
        dispatch(logInUser({ loggedInUser, authenticated: true, token }));
        onLoginSuccess("cart", sessionId, token, loggedInUser);
        onBack("cart");
      } else if (target === "signup") {
        mutateSignUp({ ...signUpData });
      } else if (target === "password") {
        setTokenState(token);
        setShowPasswordResetForm(true); // Show the password reset form
      }
    },
  });

  const { isLoading: isSignUpLoading, mutate: mutateSignUp } =
    useCirclePlugSignUp({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        const { user, token, tokenExpiryDate, tokenType, sessionId } =
          response.data;
        const loggedInUser = {
          sessionId,
          tokenExpiryDate,
          token,
          user: user,
        }
        setLoggedinUser(loggedInUser);
        dispatch(logInUser({loggedInUser, authenticated: true, token }));
        onLoginSuccess("cart", sessionId, token, loggedInUser);
        onBack("cart");
      },
    });

  const { isLoading: isLoadingResendOtp, mutate: mutateResendOtp } = useInitOtp(
    {
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: (response) => {
        const { waitTime, trackId, token } = response.data;
        setCurrentOtpData((prevData) => ({ ...prevData, waitTime, trackId }));
        setRemainingTime(Math.ceil(waitTime));
        setTokenState(token);
      },
    }
  );

  // Integrate useResetPassword mutation
  const { isLoading: isResetPasswordLoading, mutate: resetPassword } =
    useResetPassword({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: () => {
        // Handle successful password reset
        setError("");
        onBack("login"); // Redirect to login
      },
    });

  const handleOtpSubmit = useCallback(
    (e) => {
      e.preventDefault();
      mutate({ trackId: currentOtpData.trackId, code: otp, token });
    },
    [otp, token, currentOtpData, mutate]
  );

  const handlePasswordResetSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const displayName = formData.get("displayName") || "";
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    // Call the useResetPassword mutation
    resetPassword({ newPassword, token, displayName });
  };

  const handleResendOtp = useCallback(() => {
    setError("");
    mutateResendOtp({
      target,
      identifier: currentOtpData.identifier,
      type: currentOtpData.type,
      token,
    });
  }, [currentOtpData, token, mutateResendOtp, target]);

  const otpMessage = useMemo(() => {
    if (showPasswordResetForm) {
      return "Change your password";
    } else if (currentOtpData.type === "mobile") {
      return `Enter OTP sent to your mobile number ${currentOtpData.identifier}`;
    } else if (currentOtpData.type === "email") {
      return `Enter OTP sent to your email ${currentOtpData.identifier}`;
    }
    return "Enter OTP";
  }, [showPasswordResetForm, currentOtpData]);

  useEffect(() => {
    if (remainingTime > 0) {
      const timer = setTimeout(() => {
        setRemainingTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [remainingTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins > 0) {
      return `${mins}:${secs < 10 ? `0${secs}` : secs}`;
    }
    return `${secs} seconds`;
  };

  return (
    <div className="auth-form-container auth-form-container-login">
      <h2>{otpMessage}</h2>
      {error && <div className="error-message">{error}</div>}
      {!showPasswordResetForm ? (
        <form onSubmit={handleOtpSubmit}>
          <div className="input-box">
            <label htmlFor="otp">OTP:</label>
            <div className="input-text">
              <input
                type="text"
                id="otp"
                name="otp"
                value={otp}
                onChange={handleOtpChange}
                required
                placeholder="Enter OTP"
                disabled={isLoading || isLoadingResendOtp || isSignUpLoading}
              />
            </div>
          </div>
          {remainingTime > 0 ? (
            <p className="countdown-timer">
              OTP expires in {formatTime(remainingTime)}
            </p>
          ) : (
            <div className="resend-otp-container">
              <p className="error-message">
                OTP expired,
                <span className="resend-otp" onClick={handleResendOtp}>
                  {isLoadingResendOtp ? "Resending OTP..." : "Resend OTP?"}
                </span>
              </p>
            </div>
          )}
          <button
            type="submit"
            className="auth-submit-button"
            disabled={isLoading || isSignUpLoading}
          >
            {isLoading || isSignUpLoading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>
      ) : (
        <form onSubmit={handlePasswordResetSubmit}>
          <div className="auth-bot-field" aria-hidden="true">
            <label htmlFor="reset-display-name">Display name</label>
            <input
              type="text"
              id="reset-display-name"
              name="displayName"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <div className="input-box">
            <label htmlFor="newPassword">New Password:</label>
            <div className="input-text">
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
          </div>
          <div className="input-box">
            <label htmlFor="confirmPassword">Confirm Password:</label>
            <div className="input-text">
              <input
                type="password"
                id="confirmPassword"
                className="otpPasswordInput"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className="auth-submit-button"
            disabled={isResetPasswordLoading}
          >
            {isResetPasswordLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      )}
    </div>
  );
};

export default OtpForm;
